import React, { useState } from 'react'

import type { Shipment, ShipmentLeg } from '../types/deal'

const CARRIER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 't-cat', label: '黑貓 (T-Cat)' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'ups', label: 'UPS' },
  { value: 'dhl', label: 'DHL' },
]

interface ShipmentEditorProps {
  shipment: Shipment
  highlighted: boolean
  onUpdate: (next: Shipment) => void
  onDelete: () => void
  registerRef: (element: HTMLDivElement | null) => void
}

interface ShipLegRowProps {
  leg: ShipmentLeg
  shipment: Shipment
  isLast: boolean
  onChangeStatus: (status: ShipmentLeg['status']) => void
}

function statusColorFor(status: ShipmentLeg['status']): string {
  if (status === 'delivered') return '#10b981'
  if (status === 'in_transit') return '#3b82f6'
  return '#d1d5db'
}

function statusLabelColorFor(status: ShipmentLeg['status']): string {
  if (status === 'delivered') return '#10b981'
  if (status === 'in_transit') return '#3b82f6'
  return '#94a3b8'
}

const ShipLegRow: React.FunctionComponent<ShipLegRowProps> = (props) => {
  const { leg, shipment, isLast, onChangeStatus } = props
  const isCustomerLeg = leg.type === 'customer'
  const carrier = (isCustomerLeg ? leg.carrier : shipment.carrier) || shipment.carrier
  const tracking = (isCustomerLeg ? leg.trackingNumber : shipment.trackingNumber) || ''
  const dotColor = statusColorFor(leg.status)
  const labelColor = statusLabelColorFor(leg.status)

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          paddingTop: 5,
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dotColor,
            animation: leg.status === 'in_transit' ? 'pulse 1.5s ease-in-out infinite' : undefined,
            border: leg.status === 'pending' ? '1px dashed #9ca3af' : undefined,
          }}
        />
        {!isLast && <div style={{ width: 1, height: 28, background: '#e2e8f0' }} />}
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          padding: isLast ? '2px 0' : '2px 0 8px',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: '#1d1d1f' }}>
          {leg.type === 'warehouse' ? 'To Warehouse' : 'To Customer'}
        </span>
        {carrier && (
          <>
            <span style={{ fontSize: 12, color: '#64748b' }}>·</span>
            <span style={{ fontSize: 12, color: '#6e6e73' }}>{carrier}</span>
          </>
        )}
        {tracking && (
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: '#6e6e73' }}>
            {tracking}
          </span>
        )}
        <select
          value={leg.status}
          onChange={(event) => onChangeStatus(event.target.value as ShipmentLeg['status'])}
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            color: labelColor,
            border: '1px solid var(--border)',
            borderRadius: 4,
            padding: '2px 6px',
            background: 'white',
            fontFamily: 'inherit',
          }}
        >
          <option value="pending">Pending</option>
          <option value="in_transit">In Transit</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>
    </div>
  )
}

export const ShipmentEditor: React.FunctionComponent<ShipmentEditorProps> = (props) => {
  const { shipment, highlighted, onUpdate, onDelete, registerRef } = props

  const legs = shipment.legs || []
  const warehouseLeg = legs.find((leg) => leg.type === 'warehouse')
  const customerLeg = legs.find((leg) => leg.type === 'customer')
  const showAddDeliveryButton =
    !!warehouseLeg && warehouseLeg.status === 'delivered' && !customerLeg

  const [showLegForm, setShowLegForm] = useState(false)
  const [legCarrier, setLegCarrier] = useState(CARRIER_OPTIONS[1].label)
  const [legTracking, setLegTracking] = useState('')

  function changeCarrier(value: string): void {
    onUpdate({ ...shipment, carrier: value })
  }

  function changeTracking(value: string): void {
    onUpdate({ ...shipment, trackingNumber: value })
  }

  function changeInvoiceNum(value: string): void {
    onUpdate({ ...shipment, linkedInvoiceNumber: value })
  }

  function changeLegStatus(legIndex: number, status: ShipmentLeg['status']): void {
    onUpdate({
      ...shipment,
      legs: legs.map((leg, index) =>
        index === legIndex
          ? {
              ...leg,
              status,
              statusDate:
                status === 'delivered' && !leg.statusDate
                  ? new Date().toLocaleDateString()
                  : leg.statusDate,
            }
          : leg,
      ),
    })
  }

  function submitDeliveryLeg(): void {
    if (!legTracking.trim()) return
    const next: Shipment = {
      ...shipment,
      legs: [
        ...legs,
        {
          type: 'customer',
          status: 'in_transit',
          statusDate: null,
          carrier: legCarrier,
          trackingNumber: legTracking.trim(),
        },
      ],
    }
    onUpdate(next)
    setShowLegForm(false)
    setLegTracking('')
    setLegCarrier(CARRIER_OPTIONS[1].label)
  }

  return (
    <div
      ref={registerRef}
      className={`ship-card${highlighted ? ' ship-card-highlight' : ''}`}
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '20px 24px',
        marginBottom: 12,
      }}
    >
      <div className="ship-card-header" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="ship-card-title">{shipment.label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" className="action-btn danger" title="Delete" onClick={onDelete}>
            🗑
          </button>
        </div>
      </div>

      <div className="form-grid" style={{ marginBottom: 12 }}>
        <div className="form-group">
          <label className="form-label">Carrier</label>
          <select
            className="form-input"
            value={shipment.carrier}
            onChange={(event) => changeCarrier(event.target.value)}
          >
            {CARRIER_OPTIONS.map((entry) => (
              <option key={entry.value} value={entry.label}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Tracking #</label>
          <input
            className="form-input"
            value={shipment.trackingNumber ?? ''}
            placeholder="Tracking number"
            onChange={(event) => changeTracking(event.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Linked Invoice</label>
          <input
            className="form-input"
            value={shipment.linkedInvoiceNumber}
            placeholder="I#..."
            onChange={(event) => changeInvoiceNum(event.target.value)}
          />
        </div>
      </div>

      <div style={{ paddingLeft: 4 }}>
        {legs.map((leg, index) => (
          <ShipLegRow
            key={index}
            leg={leg}
            shipment={shipment}
            isLast={index === legs.length - 1 && !showAddDeliveryButton}
            onChangeStatus={(status) => changeLegStatus(index, status)}
          />
        ))}
        {showAddDeliveryButton && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 4 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                paddingTop: 5,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'transparent',
                  border: '1px dashed #9ca3af',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              {!showLegForm ? (
                <button
                  type="button"
                  onClick={() => setShowLegForm(true)}
                  style={{
                    fontSize: 12,
                    color: '#475569',
                    background: 'transparent',
                    border: '1px dashed #cbd5e1',
                    borderRadius: 6,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  + Add Delivery to Customer
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    value={legCarrier}
                    onChange={(event) => setLegCarrier(event.target.value)}
                    style={{
                      height: 28,
                      padding: '0 8px',
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      fontSize: 11,
                      fontFamily: 'inherit',
                      background: 'white',
                    }}
                  >
                    {CARRIER_OPTIONS.map((entry) => (
                      <option key={entry.value} value={entry.label}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={legTracking}
                    onChange={(event) => setLegTracking(event.target.value)}
                    placeholder="Tracking #"
                    style={{
                      height: 28,
                      padding: '0 8px',
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      fontSize: 11,
                      fontFamily: 'inherit',
                      minWidth: 160,
                    }}
                  />
                  <button
                    type="button"
                    onClick={submitDeliveryLeg}
                    style={{
                      height: 28,
                      padding: '0 12px',
                      border: '1px solid #1d1d1f',
                      background: '#1d1d1f',
                      color: 'white',
                      borderRadius: 4,
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLegForm(false)
                      setLegTracking('')
                    }}
                    style={{
                      height: 28,
                      padding: '0 12px',
                      border: '1px solid var(--border)',
                      background: 'white',
                      color: '#475569',
                      borderRadius: 4,
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
